/**
 * Task board hooks — CRUD for boards and items.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchTaskBoards,
  createTaskBoard,
  updateTaskBoard,
  deleteTaskBoard,
  createTaskItem,
  deleteTaskItem,
  toggleTaskItem,
  clearCompletedItems,
} from '@turnifly/shared/api'

export const taskKeys = {
  all: (userId: string) => ['tasks', userId] as const,
} as const

// ── Task Boards with Items ──────────────────────────────────

export function useTaskBoards(userId: string | undefined) {
  return useQuery({
    queryKey: taskKeys.all(userId ?? ''),
    queryFn: () => fetchTaskBoards(userId!),
    enabled: !!userId,
    staleTime: 120_000,
  })
}

// ── Create Board ────────────────────────────────────────────

export function useCreateTaskBoard(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ name, sortOrder }: { name: string; sortOrder: number }) =>
      createTaskBoard(userId, name, sortOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all(userId) })
    },
  })
}

// ── Update Board ────────────────────────────────────────────

export function useUpdateTaskBoard(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ boardId, name }: { boardId: string; name: string }) =>
      updateTaskBoard(boardId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all(userId) })
    },
  })
}

// ── Delete Board ────────────────────────────────────────────

export function useDeleteTaskBoard(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (boardId: string) => deleteTaskBoard(boardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all(userId) })
    },
  })
}

// ── Create Task Item ────────────────────────────────────────

export function useCreateTaskItem(userId: string) {
  const queryClient = useQueryClient()
  const key = taskKeys.all(userId)

  return useMutation({
    mutationFn: ({ boardId, text, sortOrder }: { boardId: string; text: string; sortOrder: number }) =>
      createTaskItem(boardId, userId, text, sortOrder),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: key }) },
  })
}

// ── Delete Task Item ────────────────────────────────────────

export function useDeleteTaskItem(userId: string) {
  const queryClient = useQueryClient()
  const key = taskKeys.all(userId)

  return useMutation({
    mutationFn: (itemId: string) => deleteTaskItem(itemId),
    onMutate: async (itemId: string) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)
      queryClient.setQueryData(key, (old: unknown) => {
        if (!Array.isArray(old)) return old
        return old.map((board: Record<string, unknown>) => ({
          ...board,
          items: Array.isArray(board.items) ? board.items.filter((i: Record<string, unknown>) => i.id !== itemId) : board.items,
        }))
      })
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) queryClient.setQueryData(key, ctx.previous) },
    onSettled: () => { setTimeout(() => queryClient.invalidateQueries({ queryKey: key }), 300) },
  })
}

// ── Toggle Task Item ────────────────────────────────────────

export function useToggleTaskItem(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ itemId, completed }: { itemId: string; completed: boolean }) =>
      toggleTaskItem(itemId, completed),
    onMutate: async ({ itemId, completed }) => {
      const key = taskKeys.all(userId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)

      queryClient.setQueryData(key, (old: unknown) => {
        if (!Array.isArray(old)) return old
        return old.map((board: Record<string, unknown>) => ({
          ...board,
          items: Array.isArray(board.items)
            ? board.items.map((item: Record<string, unknown>) =>
                item.id === itemId ? { ...item, completed } : item
              )
            : board.items,
        }))
      })

      return { previous, key }
    },
    onError: (_err, _params, context) => {
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all(userId) })
    },
  })
}

// ── Clear Completed Items ───────────────────────────────────

export function useClearCompletedItems(userId: string) {
  const queryClient = useQueryClient()
  const key = taskKeys.all(userId)

  return useMutation({
    mutationFn: (boardId: string) => clearCompletedItems(boardId),
    onMutate: async (boardId: string) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)
      queryClient.setQueryData(key, (old: unknown) => {
        if (!Array.isArray(old)) return old
        return old.map((board: Record<string, unknown>) =>
          board.id === boardId
            ? { ...board, items: Array.isArray(board.items) ? board.items.filter((i: Record<string, unknown>) => !i.completed) : board.items }
            : board,
        )
      })
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) queryClient.setQueryData(key, ctx.previous) },
    onSettled: () => { setTimeout(() => queryClient.invalidateQueries({ queryKey: key }), 300) },
  })
}
